import { Table, TableColumnType } from 'antd';
import React from 'react';
import { RateCheckbox } from '@institutions/components';

const deviatedTooltip =
  'Excluded items will not be updated and will not get any future rate card updates.';
const blockedTooltip = `This pending offer is excluded because the linked staffing request has been excluded. ${deviatedTooltip}`;

type DataItemType<T> = T & {
  id: string;
};

type RatesLongTermUpdatesDeviationTablePropsType<T> = {
  dataSource?: DataItemType<T>[];
  columns: TableColumnType<DataItemType<T>>[];
  deviatedIds: string[];
  onDeviateList: (ids: string[]) => void;
  onDeviateItem: (id: string) => void;
  onUndeviateItem: (id: string) => void;
  blockedDeviationIds?: string[];
};

export function RatesLongTermUpdatesDeviationTable<T>({
  dataSource,
  columns,
  deviatedIds,
  onDeviateList,
  onDeviateItem,
  onUndeviateItem,
  blockedDeviationIds = [],
}: RatesLongTermUpdatesDeviationTablePropsType<T>) {
  const checkedList = invertCheckedIds(dataSource, [
    ...deviatedIds,
    ...blockedDeviationIds,
  ]);

  const rowSelection = {
    selectedRowKeys: checkedList,
    columnWidth: '60px',
    onChange: (checkedIds: React.Key[]) => {
      const changeDeviateIds = invertCheckedIds<T>(
        dataSource,
        checkedIds as string[]
      );
      onDeviateList(changeDeviateIds);
    },
    renderCell: (checked: boolean, row: DataItemType<T>) => {
      const deviated = !checked;
      const blocked = blockedDeviationIds.includes(row.id);
      const title = blocked ? blockedTooltip : deviatedTooltip;
      return (
        <RateCheckbox
          title={title}
          checked={checked}
          blocked={blocked}
          onClick={() => {
            if (deviated) onUndeviateItem(row.id);
            else onDeviateItem(row.id);
          }}
        />
      );
    },
  };

  return (
    <Table
      rowSelection={rowSelection}
      pagination={false}
      columns={columns}
      dataSource={dataSource}
      rowKey="id"
      scroll={{ y: 422 }}
    />
  );
}

function invertCheckedIds<T>(
  dataSource?: DataItemType<T>[],
  checkedIds?: string[]
): string[] {
  if (dataSource === undefined || checkedIds === undefined) return [];
  return dataSource
    .map((item: DataItemType<T>) => item.id)
    .filter((id: string) => !checkedIds.includes(id));
}
